/* The spreadsheet import: boards T, T2 and T3 (`.drop`, `.sheets`, `.keep`). Readable labels the
 * boards set in the tertiary ink are drawn in the secondary one, as in the rest of the conversation. */

.import {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
}

.drop {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  align-items: center;
  width: 100%;
  padding: var(--space-22px);
  font-family: var(--font-body);
  color: var(--text);
  text-align: center;
  cursor: pointer;
  background: var(--bg-sunken);
  border: var(--border-thick) dashed var(--border-2);
  border-radius: var(--radius-16px);
  transition-duration: var(--dur-base);
  transition-property: background-color, border-color;
  transition-timing-function: var(--ease);
}

.drop:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.dropOver {
  background: var(--bg);
  border-color: var(--text-2);
}

.drop[aria-busy='true'] {
  cursor: default;
}

.dropGlyph {
  width: 22px;
  height: 22px;
  color: var(--text-2);
}

.dropTitle {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-extrabold);
  overflow-wrap: anywhere;
}

.dropHint {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* „Anuluj" under the zone: quiet text, the picker's own word (owner, 5 October 2026). */
.zoneCancel {
  align-self: flex-end;
  padding: var(--space-0);
  font-family: var(--font-body);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  cursor: pointer;
  background: none;
  border: 0;
}

.zoneCancel:hover {
  color: var(--text);
}

.zoneCancel:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
  border-radius: var(--radius-hair);
}

/* How the plan runs, asked above the build when no chosen sheet names weekdays (R54). */
.mode {
  padding: var(--space-3) var(--space-14px);
  border-top: var(--border-hairline) solid var(--border);
}

/* A refused choice, said in the picker above its buttons. */
.pickerRefusal {
  margin: var(--space-0) var(--space-14px) var(--space-3);
}

/* The sheet picker: each sheet a native checkbox under a drawn box, so the list takes the keyboard
 * as checkboxes do. */
.picker {
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: var(--space-0);
  padding: var(--space-0);
  overflow: hidden;
  border-color: var(--border-2);
  border-radius: var(--radius-lg);
}

.sheet {
  position: relative;
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  gap: var(--space-10px);
  align-items: center;
  padding: var(--space-10px) var(--space-4);
  font-size: var(--fs-foot);
  font-weight: var(--fw-bold);
  cursor: pointer;
  border-top: var(--border-hairline) solid var(--border);
}

.sheet:first-of-type {
  border-top: 0;
}

.sheetOff {
  cursor: default;
}

.check {
  position: absolute;
  inset: var(--space-0);
  width: 100%;
  height: 100%;
  margin: var(--space-0);
  cursor: inherit;
  opacity: 0;
}

.box {
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  border: var(--border-thick) solid var(--border-2);
  border-radius: var(--radius-5px);
}

.boxOn {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
}

.boxGlyph {
  width: 12px;
  height: 12px;
}

.sheetName {
  min-width: 0;
  overflow-wrap: anywhere;
}

.sheetOff .sheetName,
.sheetOff .box {
  opacity: 0.45;
}

.sheet:has(.check:focus-visible) .box {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.sheetMeta {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.foot {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
  padding: var(--space-3) var(--space-14px);
  border-top: var(--border-hairline) solid var(--border);
}

/* The values the plan cannot hold (board T, `.keep`). */
.values {
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  overflow: hidden;
  list-style: none;
  border-color: var(--border-2);
  border-radius: var(--radius-lg);
}

.value {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-11px) var(--space-4);
  border-top: var(--border-hairline) solid var(--border);
}

.value:first-child {
  border-top: 0;
}

.valueWhere {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.valueText {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-bold);
  overflow-wrap: anywhere;
}

.valueNote {
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}
